/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable no-unused-vars */
/* eslint-disable no-underscore-dangle */
import React, {
  useEffect, useState, useMemo, useRef
} from 'react';
import {
  Table, Tag, Empty, Skeleton, Result, Pagination, Button, Modal, Input, Select, message,
  FloatButton
} from 'antd';
import { DashboardOutlined, PlusOutlined } from '@ant-design/icons';
import { v4 as uniqueId } from 'uuid';
import useFetchData from '../../hooks/useFetchData';
import QueryOptions from '../shared/QueryOptions';
import { formatDate } from '../../utils/format';
import ApiService from '../../utils/apiService';

const colors = {
  VIP_CONCERT: 'gold-inverse',
  CONCERT: 'blue-inverse',
  CAKE: 'green-inverse',
  BUCHE: 'purple-inverse'
};

function TicketsList() {
  const [fetchAgain, setFetchAgain] = useState(false);
  const nameInputRef = useRef(null);
  const searchInputRef = useRef(null);

  const [query, setQuery] = useState({
    search: '',
    sort: 'desc',
    page: '1',
    rows: '10',
    priorite: ''
  });

  // ---- MODAL / FORM ----
  const [openModal, setOpenModal] = useState(false);
  const [form, setForm] = useState({
    name: '',
    numbers: '',
    type: 'VIP_CONCERT'
  });

  const handleCreate = async () => {
    if (!form.name || !form.numbers) {
      message.error('Veuillez remplir tous les champs');
      return;
    }

    try {
      const response = await ApiService.post('/billet', JSON.stringify(form));
      if (response?.result_code === 0) throw new Error('Erreur lors de la création');

      message.success('Ticket créé !');

      setOpenModal(false);
      setForm({ name: '', numbers: '', type: 'VIP_CONCERT' });
      setFetchAgain((x) => !x);
    } catch (err) {
      console.error(err);
      message.error(err.response?.data?.error || 'Erreur lors de la création');
    }
  };

  const [loading, error, response] = useFetchData(
    `/billets?keyword=${query.search}&limit=${query.rows}&page=${query.page}&sort=${query.sort}&priorite=${query.priorite}`,
    fetchAgain
  );

  const [openStats, setOpenStats] = useState(false);
  const [stats, setStats] = useState(null);

  const fetchStats = async () => {
    try {
      setStats(response.data.stats);
    } catch (e) {
      message.error('Impossible de charger les statistiques');
    }
  };

  useEffect(() => {
    if (openStats) {
      fetchStats();
    }
  }, [openStats]);

  useEffect(() => {
    setQuery((prev) => ({ ...prev, page: '1' }));
  }, [query.rows, query.search]);

  // Focus automatique à l'ouverture du modal
  useEffect(() => {
    if (openModal) {
      setTimeout(() => {
        nameInputRef.current?.focus();
      }, 100);
    }
  }, [openModal]);

  // Détection de la touche Enter pour ouvrir le modal
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Enter' && !openModal) {
        setOpenModal(true);
      }
      if (e.key === ' ' && !openModal && document.activeElement.tagName !== 'INPUT' && document.activeElement.tagName !== 'TEXTAREA') {
        e.preventDefault(); // empêche le scroll de la page
        // Focus + effacer
        if (searchInputRef.current) {
          searchInputRef.current.focus();
          searchInputRef.current.value = ''; // efface l'input
          setQuery((prev) => ({ ...prev, search: '' })); // met à jour le state
        }
      }
    };

    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [openModal]);

  const handlePayNumber = async (ticketId, number, isPaid) => {
    try {
      if (isPaid) {
      // Si déjà payé, on dé-paye
        await ApiService.put(`/billet/unpay/${ticketId}`, { ticketId, number });
        message.success(`Numéro ${number} remis comme non payé`);
      } else {
      // Si non payé, on paye
        await ApiService.put(`/billet/pay/${ticketId}`, { ticketId, number });
        message.success(`Numéro ${number} payé`);
      }

      // Rafraîchir le tableau
      setFetchAgain((x) => !x);
    } catch (err) {
      console.error(err);
      message.error(err.response?.data?.message || 'Erreur lors de la modification');
    }
  };

  const handleDelete = async (ticketId) => {
    try {
      // Si déjà payé, on dé-paye
      const res = await ApiService.delete(`/billet/${ticketId}`);
      message.success(res.response?.data?.message || 'Ticket supprimé');

      // Rafraîchir le tableau
      setFetchAgain((x) => !x);
    } catch (err) {
      console.error(err);
      message.error(err.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  // ----------------------------
  // Colonnes du tableau
  // ----------------------------
  const columns = useMemo(() => [
    {
      title: 'Nom',
      dataIndex: 'name',
      key: 'name',
      render: (v) => <span className='font-semibold'>{v}</span>
    },
    {
      title: 'Numéros',
      dataIndex: 'ticketNumbers',
      key: 'ticketNumbers',
      render: (arr, record) => (
        <span>
          {arr.map((n) => {
            const isPaid = record.paidNumbers.includes(n);
            return (
              <Tag
                key={n}
                color={isPaid ? 'green' : 'blue'}
                className='mr-1 cursor-pointer select-none'
                onDoubleClick={() => {
                // Double clic → payer si non payé, dé-payer si déjà payé
                  handlePayNumber(record._id, n, isPaid);
                }}
                onContextMenu={(e) => {
                // Clic droit → dé-payer (prévenir menu contextuel)
                  e.preventDefault();
                  if (isPaid) {
                    handlePayNumber(record._id, n, isPaid); // utilise la même fonction
                  } else {
                    message.info(`Numéro ${n} n’est pas encore payé`);
                  }
                }}
              >
                {n}
              </Tag>

            );
          })}
        </span>
      )
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type) => <Tag className='select-none' color={colors[type] || 'default'}>{type}</Tag>
    },
    {
      title: 'Payé le',
      dataIndex: 'paidAt',
      key: 'paidAt',
      render: (d) => (d ? formatDate(d) : '-')
    },
    {
      title: 'Créé',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (d) => formatDate(d)
    }
  ], [response?.data?.rows]);

  return (
    <div className='w-full'>

      {/* ---- Bouton Ajouter ---- */}
      {/* ---- Bouton Ajouter flottant ---- */}
      <FloatButton
        icon={<PlusOutlined />}
        type='primary'
        tooltip='Créer un ticket'
        onClick={() => setOpenModal(true)}
        style={{
          right: 24,
          bottom: 24
        }}
      />
      <FloatButton
        icon={<DashboardOutlined />}
        type='default'
        tooltip='Statistiques'
        onClick={() => setOpenStats(true)}
        style={{
          right: 24,
          bottom: 90
        }}
      />

      {/* Options de recherche / filtres */}
      <QueryOptions query={query} setQuery={setQuery} searchInputRef={searchInputRef} />
      <div className='flex gap-3 mb-3 px-2'>
        <Select
          value={query.priorite}
          style={{ width: 220 }}
          onChange={(v) => setQuery((prev) => ({ ...prev, priorite: v }))}
          options={[
            { value: '', label: 'Ajouter recemment' },
            { value: 'maxPaid', label: 'Plus de numéros payés' }
          ]}
        />
      </div>

      {error ? (
        <Result title='Erreur' subTitle={error} status='error' />
      ) : (
        <Skeleton loading={loading} active paragraph={{ rows: 10 }}>
          {response?.data?.rows?.length === 0 ? (
            <Empty description='Aucun ticket trouvé' />
          ) : (
            <Table
              rowKey={() => uniqueId()}
              columns={columns}
              dataSource={response?.data?.rows}
              pagination={false}
              scroll={{ x: 1000 }}
              bordered
              className='shadow-lg rounded-xl'
              onRow={(record) => {
                let pressTimer;

                return {
                  onMouseDown: () => {
                    // Démarre le timer pour le long press
                    pressTimer = setTimeout(() => {
                      Modal.confirm({
                        title: 'Supprimer cet élément ?',
                        content: `Voulez-vous supprimer ${record.name} ?`,
                        okText: 'Supprimer',
                        okType: 'danger',
                        cancelText: 'Annuler',
                        onOk: () => handleDelete(record._id)
                      });
                    }, 800); // 800ms = durée du long press
                  },
                  onMouseUp: () => {
                    clearTimeout(pressTimer); // si relâché avant 800ms, annule
                  },
                  onMouseLeave: () => {
                    clearTimeout(pressTimer); // si la souris sort de la ligne, annule
                  }
                };
              }}
            />

          )}
        </Skeleton>
      )}

      {/* Pagination */}
      {response?.data?.total_page > 1 && (
        <Pagination
          className='my-5 flex justify-center'
          onChange={(e) => setQuery((prev) => ({ ...prev, page: e }))}
          total={response?.data?.total_page * parseInt(query.rows, 10)}
          current={parseInt(response?.data?.current_page, 10)}
        />
      )}

      {/* ---- MODAL ---- */}
      <Modal
        title='Créer un Ticket'
        open={openModal}
        onCancel={() => setOpenModal(false)}
        onOk={handleCreate}
        okText='Créer'
      >
        <div className='flex flex-col gap-3'>
          <label>Nom</label>
          <Input
            ref={nameInputRef}
            placeholder='Ex: Me Olivia Itaosy'
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />

          <label>Numéros (ex: 10-20, 33, 45-47)</label>
          <Input
            placeholder='Ex: 1-5, 8, 10-12'
            value={form.numbers}
            onChange={(e) => setForm({ ...form, numbers: e.target.value })}
          />

          <label>Type</label>
          <Select
            value={form.type}
            onChange={(v) => setForm({ ...form, type: v })}
            options={[
              { value: 'VIP_CONCERT', label: 'VIP CONCERT' },
              { value: 'CONCERT', label: 'CONCERT' },
              { value: 'CAKE', label: 'CAKE' },
              { value: 'BUCHE', label: 'BUCHE' }
            ]}
          />
        </div>
      </Modal>
      <Modal
        title='Statistiques des Tickets'
        open={openStats}
        onCancel={() => setOpenStats(false)}
        footer={null}
      >
        {!stats ? (
          <Skeleton active />
        ) : (
          <div className='flex flex-col gap-4'>

            <div className='p-3 border rounded-lg'>
              <h3 className='font-bold mb-2'>Général</h3>
              <p>
                Total tickets :
                <b className='ml-3'>{stats.totalTickets}</b>
              </p>
              <p>
                Total payés :
                <b className='ml-3'>{stats.totalPaid}</b>
              </p>
              <p>
                Total non payés :
                <b className='ml-3'>{stats.totalUnpaid}</b>
              </p>
            </div>

            <div className='p-3 border rounded-lg'>
              {stats.byType?.map((t) => (
                <div key={t._id} className='mb-3 pb-2 border-b'>
                  <Tag
                    className='select-none mb-2'
                    color={colors[t._id] || 'default'}
                  >
                    {t._id}
                  </Tag>

                  {/* GRID avec pointillés */}
                  <div className='ml-3 flex flex-col gap-1'>
                    {[
                      { label: 'Total', value: t.totalTickets },
                      { label: 'Payés', value: t.totalPaid },
                      { label: 'Non payés', value: t.totalUnpaid }
                    ].map((item) => (
                      <div key={item.label} className='flex justify-between items-center'>
                        <span className='text-gray-600'>{item.label}</span>
                        {/* pointillés */}
                        <span className='mt-2 flex-1 border-b border-dotted border-gray-400 mx-2' />
                        <b className='text-right'>{item.value}</b>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

          </div>
        )}
      </Modal>

    </div>
  );
}

export default React.memo(TicketsList);
